import { useState, useEffect, useCallback } from 'react';
import { ExclamationTriangleIcon, PlayIcon, CheckCircleIcon, AdjustmentsHorizontalIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { alertApi, AlertStatus, AlertSeverity, AlertType } from '../api/alertService';
import type { Alert, AlertStats as AlertStatsType } from '../api/alertService';
import AlertCard from './AlertCard';
import AlertStats from './AlertStats';
import AlertFilters from './AlertFilters';
import AlertAdvancedFilterModal from './AlertAdvancedFilterModal';

interface FilterOptions {
  status: AlertStatus | 'all';
  severity: AlertSeverity | 'all';
  type: AlertType | 'all';
  gatewayId: string | 'all';
  customerId: string | 'all';
  sortBy: 'newest' | 'oldest' | 'severity' | 'status';
}

export default function AlertsList() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [stats, setStats] = useState<AlertStatsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [triggeringCheck, setTriggeringCheck] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalAlerts, setTotalAlerts] = useState(0);
  const pageSize = 20;

  const [filters, setFilters] = useState<FilterOptions>({
    status: 'all',
    severity: 'all',
    type: 'all',
    gatewayId: 'all',
    customerId: 'all',
    sortBy: 'newest',
  });

  const fetchAlerts = useCallback(async (page: number = 1) => {
    try {
      setError(null);
      const offset = (page - 1) * pageSize;
      
      const params: any = {
        limit: pageSize,
        offset,
      };
      
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.gatewayId !== 'all') params.gatewayId = filters.gatewayId;
      if (filters.customerId !== 'all') params.customerId = filters.customerId;
      
      const response = await alertApi.getAll(params);
      
      if (response.success) {
        setAlerts(response.data);
        setTotalAlerts(response.total);
      } else {
        setError(response.error || 'Failed to fetch alerts');
      }
    } catch (err) {
      console.error('Error fetching alerts:', err);
      setError('Failed to fetch alerts');
    }
  }, [filters.status, filters.gatewayId, filters.customerId, pageSize]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await alertApi.getStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Error fetching alert stats:', err);
    }
  }, []);

  const loadData = useCallback(async (page: number = 1) => {
    setLoading(true);
    await Promise.all([fetchAlerts(page), fetchStats()]);
    setLoading(false);
  }, [fetchAlerts, fetchStats]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchAlerts(page);
  };

  const handleAdvancedFiltersApply = (newFilters: FilterOptions) => {
    setFilters(newFilters);
    // Page reset and data fetch will be handled by useEffect
  };

  const refreshData = async () => {
    setRefreshing(true);
    await Promise.all([fetchAlerts(currentPage), fetchStats()]);
    setRefreshing(false);
  };

  const triggerAlertCheck = async () => {
    setTriggeringCheck(true);
    try {
      const response = await alertApi.triggerCheck();
      if (response.success) {
        // Refresh data after triggering check
        setTimeout(() => {
          fetchAlerts(currentPage);
          fetchStats();
        }, 2000); // Wait 2 seconds for the check to complete
      } else {
        setError(response.error || 'Failed to trigger alert check');
      }
    } catch (err) {
      console.error('Error triggering alert check:', err);
      setError('Failed to trigger alert check');
    } finally {
      setTriggeringCheck(false);
    }
  };

  const handleAcknowledge = async (alertId: string) => {
    // Optimistically update the UI
    setAlerts(prevAlerts => 
      prevAlerts.map(alert => 
        alert.id === alertId 
          ? { ...alert, status: AlertStatus.ACKNOWLEDGED, acknowledgedAt: new Date().toISOString(), acknowledgedBy: 'Current User' }
          : alert
      )
    );

    // Refresh stats
    await fetchStats();
  };

  // Filter and sort alerts (client-side for non-backend filters)
  const filteredAndSortedAlerts = alerts
    .filter(alert => {
      if (filters.severity !== 'all' && alert.severity !== filters.severity) return false;
      if (filters.type !== 'all' && alert.type !== filters.type) return false;
      return true;
    })
    .sort((a, b) => {
      switch (filters.sortBy) {
        case 'oldest':
          return new Date(a.firstDetectedAt).getTime() - new Date(b.firstDetectedAt).getTime();
        case 'severity':
          const severityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
          return severityOrder[b.severity] - severityOrder[a.severity];
        case 'status':
          const statusOrder = { active: 3, acknowledged: 2, resolved: 1 };
          return statusOrder[b.status] - statusOrder[a.status];
        case 'newest':
        default:
          return new Date(b.firstDetectedAt).getTime() - new Date(a.firstDetectedAt).getTime();
      }
    });

  const totalPages = Math.ceil(totalAlerts / pageSize);

  // Load data when backend filters change (reset to page 1)
  useEffect(() => {
    setCurrentPage(1);
    fetchAlerts(1);
    fetchStats();
  }, [filters.status, filters.gatewayId, filters.customerId, fetchAlerts, fetchStats]);

  // Load data when page changes (but filters stay the same)
  useEffect(() => {
    if (currentPage > 1) { // Only fetch if not page 1 (already handled above)
      fetchAlerts(currentPage);
    }
  }, [currentPage, fetchAlerts]);

  // Initial load
  useEffect(() => {
    loadData(1);
  }, []);

  // Refresh alerts when client-side filters change (severity, type, sortBy)
  useEffect(() => {
    // No need to refetch from backend, just trigger re-render
  }, [filters.severity, filters.type, filters.sortBy]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (!loading && !refreshing) {
        fetchStats(); // Only refresh stats to avoid UI jumps
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [loading, refreshing, fetchStats]);

  if (loading && !stats) {
    return (
      <div className="space-y-6">
        <AlertStats stats={{} as AlertStatsType} loading={true} />
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 animate-pulse">
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-gray-300 rounded-lg"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-300 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Message */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Overview */}
      {stats && <AlertStats stats={stats} loading={false} />}

      {/* Action Buttons */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium text-gray-900">Alert Management</h2>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowAdvancedFilters(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <AdjustmentsHorizontalIcon className="h-4 w-4 mr-2" />
            Advanced Filters
          </button>
          <button
            onClick={triggerAlertCheck}
            disabled={triggeringCheck}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {triggeringCheck ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Running Check...
              </>
            ) : (
              <>
                <PlayIcon className="h-4 w-4 mr-2" />
                Run Alert Check
              </>
            )}
          </button>
        </div>
      </div>

      {/* Filters */}
      <AlertFilters 
        filters={filters} 
        onFiltersChange={setFilters}
        onRefresh={refreshData}
        loading={refreshing}
      />

      {/* Alerts List */}
      <div className="space-y-4">
        {filteredAndSortedAlerts.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircleIcon className="mx-auto h-12 w-12 text-green-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No alerts found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {filters.status !== 'all' || filters.severity !== 'all' || filters.type !== 'all'
                ? 'Try adjusting your filters to see more alerts.'
                : 'All systems are running smoothly!'}
            </p>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-500">
                Showing {filteredAndSortedAlerts.length} of {totalAlerts} alerts (Page {currentPage} of {totalPages})
              </p>
              <div className="text-xs text-gray-500">
                Last updated: {new Date().toLocaleTimeString()}
              </div>
            </div>
            
            {filteredAndSortedAlerts.map((alert) => (
              <AlertCard
                key={alert.id}
                alert={alert}
                onAcknowledge={handleAcknowledge}
                showActions={true}
              />
            ))}
          </>
        )}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Page <span className="font-medium">{currentPage}</span> of{' '}
                <span className="font-medium">{totalPages}</span>
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>
                
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  if (pageNum > totalPages) return null;
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                        pageNum === currentPage
                          ? 'z-10 bg-blue-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                          : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Footer Info */}
      <div className="text-center text-xs text-gray-500 pt-4 border-t border-gray-200">
        <p>
          Alerts are automatically checked every 5 minutes. 
          Use "Run Alert Check" to manually trigger an immediate check.
        </p>
      </div>

      {/* Advanced Filter Modal */}
      <AlertAdvancedFilterModal
        isOpen={showAdvancedFilters}
        onClose={() => setShowAdvancedFilters(false)}
        onApplyFilters={handleAdvancedFiltersApply}
        currentFilters={filters}
      />
    </div>
  );
}